// src/utils/paidPartialRecovery.js
//
// Deduplicação silenciosa de partials pagos.
// Quando o paciente reentra no funil (ou abre /consulta/chat) com identidade
// que JÁ corresponde a um partial PAGO em aberto, este utilitário adota o
// patientId existente em LS+SS (sobrescrevendo o novo que o NameForm criou)
// e devolve a info necessária para o caller redirecionar direto ao chat.
//
// Caso real (06/06/2026 — Cristiane): paciente pagou (chat #1), voltou ~7h30
// depois sem LS, reabriu o funil e criou um SEGUNDO partial com mesmo email.
// Este módulo é o ponto único onde a recuperação é feita, evitando duplicação
// de lógica entre EmailConfirmForm, EmailForm e ConsultaChat (bootstrap).
//
// Sem modal, sem OTP — apenas reidratação automática quando o backend
// confirma que existe um partial pago/completed nas últimas 48h.

import { persistIdentityKey } from './identityBackup';
import { logUxEvent } from '../telemetry/uxLogger';

const PAID_DUP_WINDOW_HOURS = 48;
const DOCTOR_ID = 'DrGuilhermeEsmanhotto';

/**
 * Rota pós-recuperação de partial pago. Pacientes paid/completed NUNCA voltam
 * ao /cpf — vão direto ao chat pago (caso Daniela 10/06/2026).
 */
export function resolvePaidRecoveryRoute(info) {
  const status = String(info?.status || info?.existingStatus || '').toLowerCase();
  if (status === 'paid' || status === 'completed') {
    return '/consulta/chat/pago';
  }
  if (info?.channelOpen || info?.activeChannelId) {
    return '/consulta/chat';
  }
  return '/cpf';
}

function defaultChannelIdForPatient(patientId) {
  const pid = String(patientId || '').trim();
  return pid ? `${pid}-${DOCTOR_ID}` : null;
}

/**
 * Sobrescreve a identidade local com a do partial existente (paid/completed).
 * Idempotente: se o patientId já é o mesmo, atualiza apenas activeChannelId.
 *
 * @param {object} info
 *   - existingPatientId: string (obrigatório)
 *   - firstName?: string (atualiza patientName visível)
 *   - activeChannelId?: string (do backend; ativa retomada do chat)
 *   - source: string (telemetria — 'email_lookup' | '409_start' | 'chat_boot')
 *   - newlyCreatedPatientId?: string (o id "fantasma" que estamos descartando)
 */
export function adoptExistingPaidPatientId(info) {
  if (!info || !info.existingPatientId) return false;
  const existing = String(info.existingPatientId).trim();
  if (!existing) return false;

  let priorPatientId = null;
  try {
    priorPatientId =
      localStorage.getItem('patientId') ||
      sessionStorage.getItem('patientId') ||
      null;
  } catch {}

  // Atualiza patientId nas 4 camadas (LS + SS + cookie + IDB).
  try { persistIdentityKey('patientId', existing); } catch {}

  // Repovoa activeChannelId para PatientLayout retomar o chat. Para paid/completed
  // usamos o canal canônico mesmo se channelOpen=false (backend pode reabrir).
  const status = String(info.status || '').toLowerCase();
  const channelId =
    info.activeChannelId ||
    ((status === 'paid' || status === 'completed') ? defaultChannelIdForPatient(existing) : null);
  if (channelId) {
    try { localStorage.setItem('activeChannelId', String(channelId)); } catch {}
    try { sessionStorage.setItem('activeChannelId', String(channelId)); } catch {}
  }

  // Se backend devolveu firstName, mantém o nome do partial pago (evita
  // que o nome digitado no NameForm sobrescreva o nome original do chat).
  if (info.firstName) {
    try {
      // Só atualiza se o nome existente diverge (preserva sobrenome do partial).
      const current = localStorage.getItem('fullName') || '';
      const currentFirst = String(current || '').trim().split(/\s+/)[0] || '';
      if (currentFirst.toLowerCase() !== String(info.firstName).toLowerCase()) {
        // Mantém o nome atual — não temos o nome completo do backend, só
        // o primeiro. Resultado: NameForm pode ter gravado nome diferente,
        // mas o canal Stream já tem o nome original. Sem ação destrutiva.
      }
    } catch {}
  }

  try {
    logUxEvent('paid_dup_auto_recovered', {
      source: String(info.source || 'unknown'),
      existing_patient_id: existing.slice(0, 80),
      from_patient_id: priorPatientId && priorPatientId !== existing
        ? String(priorPatientId).slice(0, 80)
        : null,
      new_id_discarded: info.newlyCreatedPatientId
        ? String(info.newlyCreatedPatientId).slice(0, 80)
        : null,
      channel_open: !!(info.channelOpen || info.activeChannelId),
      recovery_route: resolvePaidRecoveryRoute(info),
      hours_ago: typeof info.hoursAgo === 'number' ? info.hoursAgo : null,
    });
  } catch {}

  return true;
}

/**
 * Chama POST /registration/lookup com email/cpf/phone e decide se há partial
 * pago em aberto para adotar. Retorna o payload (com flags) quando há
 * recovery elegível; null caso contrário.
 *
 * Critério de elegibilidade:
 *   - HTTP 200 + success
 *   - status === 'paid' ou 'completed'
 *   - hoursSinceLastStep < 48 (ou backend não devolveu, aceitamos)
 *
 * @param {string} baseUrl - origem do API (BACKEND_URL no caller)
 * @param {object} identity - { email?, cpf?, phone? }
 * @param {object} [opts] - { timeoutMs?: number }
 */
export async function tryRecoverPaidPatientId(baseUrl, identity, opts = {}) {
  if (!baseUrl) return null;
  const email = String(identity?.email || '').trim().toLowerCase();
  const cpf = String(identity?.cpf || '').replace(/\D/g, '');
  const phone = String(identity?.phone || '').replace(/\D/g, '');
  if (!email && cpf.length !== 11 && phone.length < 10) return null;

  const timeoutMs = Number(opts.timeoutMs || 4000);
  let ctrl = null;
  let timeoutId = null;
  try {
    ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    timeoutId = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null;
    const res = await fetch(`${baseUrl}/registration/lookup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email || undefined,
        cpf: cpf || undefined,
        phone: phone || undefined,
      }),
      signal: ctrl?.signal,
    });
    if (timeoutId) clearTimeout(timeoutId);

    if (!res.ok) return null;
    const data = await res.json().catch(() => ({}));
    if (!data || !data.success || !data.patientId) return null;

    const status = String(data.status || '').toLowerCase();
    if (status !== 'paid' && status !== 'completed') return null;

    const hours = typeof data.hoursSinceLastStep === 'number'
      ? data.hoursSinceLastStep
      : null;
    if (hours !== null && hours > PAID_DUP_WINDOW_HOURS) return null;

    return {
      existingPatientId: String(data.patientId),
      firstName: data.firstName || null,
      status,
      firstPaidAt: data.firstPaidAt || null,
      activeChannelId: data.activeChannelId || null,
      channelOpen: !!data.channelOpen,
      hoursAgo: hours,
    };
  } catch (err) {
    if (timeoutId) try { clearTimeout(timeoutId); } catch {}
    // Telemetria de falha silenciosa (não bloqueia o cadastro).
    try {
      logUxEvent('paid_dup_lookup_failed', {
        reason: err?.name === 'AbortError' ? 'timeout' : (err?.message || 'unknown'),
      });
    } catch {}
    return null;
  }
}

/**
 * Trata payload do 409 retornado pelo backend em /registration/start
 * (defesa redundante quando o lookup do frontend não pegou — ex.: race
 * de cliente antigo / cache de versão). O backend devolve a mesma estrutura
 * usada pelo lookup, então podemos adotar via mesmo helper.
 */
export function adoptFromBackend409Payload(payload, sourceLabel) {
  if (!payload) return false;
  if (!payload.existingPatientId) return false;
  return adoptExistingPaidPatientId({
    existingPatientId: payload.existingPatientId,
    firstName: payload.firstName || null,
    activeChannelId: payload.activeChannelId || null,
    status: payload.existingStatus || payload.status || null,
    hoursAgo: typeof payload.hoursAgo === 'number' ? payload.hoursAgo : null,
    source: sourceLabel || '409_start',
  });
}

export const PAID_DUP_RECOVERY_WINDOW_HOURS = PAID_DUP_WINDOW_HOURS;
